import React, { useContext } from 'react';
import { MissionDebugItem } from '../types/missionDebug';

/**
 * Defines the shape of the context value provided by MissionDebugProvider.
 */
export interface MissionDebugContextValue {
  /**
   * Function provided by the context provider for hooks to register their state/actions.
   * @param key A unique key identifying the state/action within the component.
   * @param item The state item (value getter, setter, metadata) or action item (function, metadata).
   */
  registerItem: (key: string, item: MissionDebugItem) => void;
}

/**
 * React Context for mission debugging.
 * Hooks like useMissionDebugState will consume this context to register themselves
 * with the nearest MissionDebugProvider.
 */
const MissionDebugContext = React.createContext<MissionDebugContextValue | null>(
  null
);

/**
 * Custom hook to safely consume the MissionDebugContext.
 * Throws an error if used outside of a MissionDebugProvider.
 * @returns The context value containing the registerItem function.
 */
export const useMissionDebugContext = (): MissionDebugContextValue => {
  const context = useContext(MissionDebugContext);
  if (!context) {
    throw new Error(
      'useMissionDebugContext must be used within a MissionDebugProvider'
    );
  }
  return context;
};

export default MissionDebugContext; 